//May8 ImageFieldSettings:
import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { AddPhotoAlternate, Upload, Close } from '@mui/icons-material';
import ImageIcon from '@mui/icons-material/Image';
import PanoramaIcon from '@mui/icons-material/Panorama';
import AssetLibrary from '../../../../AssetManager/AssetLibrary';
import AssetUploadDialog from '../../../../AssetManager/AssetUploadDialog';
import { useFieldEdit } from '../../../../contexts/FieldEditContext';
import debounce from 'lodash/debounce';
import '../../../../AssetManager/styles/assetCard.css';
import '../../../../AssetManager/styles/assetManager.css';
import { useStorageUrl } from '../../../../hooks/useStorageUrl';
import {
  clearImageFieldAssetMetadata,
  normalizeImageFieldMetadata,
} from '../../../../utils/assetFieldMetadata';

const ImageFieldSettings = () => {
  const { selectedFieldForEdit, updateField } = useFieldEdit();
  console.log('[ImageFieldSettings: debug] Received selectedFieldForEdit:', selectedFieldForEdit);
  const openAssetLibrary = () => setIsLibraryOpen(true);

  // Initialize local state with default values
  const [localValues, setLocalValues] = useState({
    label: '',
    dimensions: null, 
    style: {
      borderColor: '#ccc',
      borderWidth: '1px',
      borderRadius: '8px',
      padding: '2px', // Default Padding for spacing between image and border
      marginTop: '0px',
      marginBottom: '0px',
      marginLeft: '0px',
      marginRight: '0px',
    },
    allowedImageTypes: ['icon'], // Default to 'icon' for new fields
  });

  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const thumbPath = selectedFieldForEdit?.metadata?.thumbnail || null;

  /* Prefer direct browser URL if present.
     This is now the canonical fast path for selected public form-image assets. */
  const directThumbUrl = selectedFieldForEdit?.metadata?.thumbUrl || null;

  // ENTERPRISE:
  // Derive the image asset type from persisted metadata, not UI labels.
  // This aligns with the backend/public-read policy and Stage 5 system type model.
  const selectedPreviewAssetType =
    selectedFieldForEdit?.metadata?.assetReference?.systemAssetType ||
    selectedFieldForEdit?.metadata?.assetReference?.assetType ||
    selectedFieldForEdit?.metadata?.systemAssetType ||
    selectedFieldForEdit?.metadata?.assetType ||
    (
      Array.isArray(selectedFieldForEdit?.metadata?.allowedImageTypes)
        ? selectedFieldForEdit.metadata.allowedImageTypes[0]
        : null
    ) ||
    'icon';

  // If we already have a browser-usable URL, do not resolve the raw Storage path.
  // This avoids unnecessary Firebase Storage SDK getDownloadURL() calls and the
  // resulting storage/unauthorized console noise for public form-image assets.
  const thumbPathForResolution = directThumbUrl ? null : thumbPath;

  /* Resolve the stored *path* to a browser-usable URL only when needed */
  const {
    url: pathThumbUrl,
    error: thumbErr,
    source: thumbSource,
  } = useStorageUrl(thumbPathForResolution, {
    assetType: selectedPreviewAssetType,
    preferPublicUrl: true,
    allowPublicFallback: true,
    debugLabel: 'ImageFieldSettings.thumbnail',
  });

  /* Optional: tiny log while you’re validating */
  console.log('[ImageFieldSettings] directThumbUrl:', directThumbUrl,
              'pathThumbUrl:', pathThumbUrl,
              'thumbErr:', thumbErr,
              'thumbSource:', thumbSource,
              'thumbPathForResolution:', thumbPathForResolution);

  // 1×1 transparent SVG – zero network requests, never 404s
  const DATA_URI_PLACEHOLDER =
    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/>';

  // Sync local state with selectedFieldForEdit when it changes
  useEffect(() => {
    if (selectedFieldForEdit && selectedFieldForEdit.metadata) {
    console.log('[ImageFieldSettings: debug] Synced metadata:', selectedFieldForEdit.metadata);
      const updatedAllowedImageTypes = Array.isArray(selectedFieldForEdit.metadata.allowedImageTypes)
        ? [selectedFieldForEdit.metadata.allowedImageTypes[0] || 'icon'] // Ensure single type, defaulting to 'icon'
        : ['icon']; // Default to 'icon' if no valid type exists

      setLocalValues({
        label: selectedFieldForEdit.metadata.label || '',
        dimensions : selectedFieldForEdit.metadata.dimensions || null,
        style: {
          borderColor: selectedFieldForEdit.metadata.style?.borderColor || '#ccc',
          borderWidth: selectedFieldForEdit.metadata.style?.borderWidth || '1px',
          borderRadius: selectedFieldForEdit.metadata.style?.borderRadius || '8px',
          padding: selectedFieldForEdit.metadata.style?.padding || '2px',
          marginTop: selectedFieldForEdit.metadata.style?.marginTop || '0px',
          marginBottom: selectedFieldForEdit.metadata.style?.marginBottom || '0px',
          marginLeft: selectedFieldForEdit.metadata.style?.marginLeft || '0px',
          marginRight: selectedFieldForEdit.metadata.style?.marginRight || '0px',
        },
        allowedImageTypes: updatedAllowedImageTypes,
      });
    }
  }, [selectedFieldForEdit]);

  // Debounced field update function to optimize state synchronization with context
  const debouncedUpdateField = useCallback(
    debounce((id, newMetadata) => {
      updateField(id, newMetadata);
      console.log('Field updated:', id, newMetadata);
    }, 300),
    [updateField]
  );

  // Handles metadata changes, ensuring single selection for allowedImageTypes
  const handleChange = useCallback(
    (key, value) => {
    console.log(`[ImageFieldSettings: debug] handleChange triggered with key: ${key}, value:`, value); // ✅ ADD THIS

      setLocalValues((prevValues) => {
        const updatedValues = {
          ...prevValues,
          [key]: key === 'allowedImageTypes' ? [value] : value, // Ensure single selection for image types
        };

        debouncedUpdateField(selectedFieldForEdit.id, {
          ...selectedFieldForEdit.metadata,
          ...updatedValues,
        });
// Add this log AFTER the call
console.log('[ImageFieldSettings: debug] Calling updateField with:', {
  id: selectedFieldForEdit.id,
  updates: {
    ...selectedFieldForEdit.metadata,
    ...updatedValues,
  }
});
        return updatedValues;
      });
    },
    [selectedFieldForEdit?.id, selectedFieldForEdit?.metadata, debouncedUpdateField]
  );

  // Handles style property changes
  const handleStyleChange = useCallback(
    (key, value) => {
      setLocalValues((prevValues) => {
        const updatedStyle = {
          ...prevValues.style,
          [key]: key === 'borderColor' ? value : `${value}px`, // Add 'px' only for numeric styles
        };

        console.log('[ImageFieldSettings] Updating style:', updatedStyle);

        debouncedUpdateField(selectedFieldForEdit.id, {
          ...selectedFieldForEdit.metadata,
          style: updatedStyle,
        });

        return { ...prevValues, style: updatedStyle };
      });
    },
    [selectedFieldForEdit?.id, selectedFieldForEdit?.metadata, debouncedUpdateField]
  );

  // Cancel debounce on component unmount
  useEffect(() => {
    return () => debouncedUpdateField.cancel();
  }, [debouncedUpdateField]);

  // NEW: apply selection in ONE atomic update
  const applyAssetSelection = React.useCallback((
    asset,
    intrinsicDims,
    source = 'asset-library',
  ) => {
    if (!selectedFieldForEdit?.id) return;

    // derive dimensions (prefer provided → metadata → original)
    const dims =
      intrinsicDims ??
      asset?.metadata?.dimensions ??
      asset?.dimensions ??
      {
        width : asset?.versions?.original?.width  || 128,
        height: asset?.versions?.original?.height || 128,
      };

    // ENTERPRISE: derive selected type from the field’s source-of-truth metadata
    // (avoids closure drift if localValues changes between renders)
    const selectedTypeFromMeta =
      Array.isArray(selectedFieldForEdit?.metadata?.allowedImageTypes)
        ? (selectedFieldForEdit.metadata.allowedImageTypes[0] || 'icon')
        : 'icon';

    const nextMetadata = normalizeImageFieldMetadata(
      selectedFieldForEdit.metadata,
      asset,
      {
        allowedImageTypes: [selectedTypeFromMeta],
        assetType: selectedTypeFromMeta,
        dimensions: dims,
        source,
      },
    );

    // sync local UI (so style panel shows new dims)
    setLocalValues(prev => ({
      ...prev,
      dimensions: nextMetadata.dimensions || dims,
    }));

    updateField(selectedFieldForEdit.id, nextMetadata);

  }, [selectedFieldForEdit?.id, selectedFieldForEdit?.metadata, updateField]);

// Render selected image or placeholder
const renderSelectedAsset = () => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}>
        <CircularProgress />
      </Box>
    );
  }

  /* ──────────────────────────────────────────────────────────
     Selected image → show thumbnail with hover “×” to remove
     ────────────────────────────────────────────────────────── */
   if (selectedFieldForEdit?.metadata?.assetReference) {
    // Determine selected type (icon vs banner) so preview can mimic the builder
    const selectedType = localValues.allowedImageTypes[0] || 'icon';
    const isIcon       = selectedType === 'icon';

    // ── convert the stored *path* to a temporary download URL ─────────
    const displayUrl = directThumbUrl || pathThumbUrl || DATA_URI_PLACEHOLDER;

    return (
      <Box
        sx={{
          position      : 'relative',
          width         : 96,
          height        : 96,
          borderRadius  : 2,
          overflow      : 'hidden',
          border        : '1px solid',
          borderColor   : 'divider',
          mb            : 2,
          cursor        : 'pointer',

          /* subtle elevation + colour shift on hover */
          transition    : theme =>
            theme.transitions.create(['box-shadow', 'border-color']),
          '&:hover'     : {
            boxShadow  : 3,
            borderColor: 'primary.main',
          },

          /* reveal the close icon only on hover / focus */
          '&:hover .remove-btn, &:focus-within .remove-btn': { opacity: 1 },
        }}
        onClick={openAssetLibrary}               // reopen library on thumbnail click
        className="asset-card-container asset-card-selected"
      >
        {/* Thumbnail image */}
        <img
          src={displayUrl}
          alt={selectedFieldForEdit.metadata.label || 'Selected image'}
          style={{
            width : '100%',
            height: '100%',
            // Icons: show full image (no crop, may letterbox)
            // Banners: keep hero-style cropped preview
            objectFit: isIcon ? 'contain' : 'cover',
          }}
          className="asset-card-image"
        />


        {/* “×” icon – hidden until hover/focus */}
        <IconButton
          className="remove-btn"
          size="small"
          aria-label="Remove image"
          onClick={(e) => {
            e.stopPropagation(); // don’t trigger openAssetLibrary
            console.log('[ImageFieldSettings] Remove thumbnail clicked');

            const cleared = clearImageFieldAssetMetadata(
              selectedFieldForEdit.metadata,
            );

            setLocalValues(prev => ({
              ...prev,
              dimensions: null,
            }));

            updateField(selectedFieldForEdit.id, cleared);
          }}

          sx={{
            p         : '2px',                  // ↓ default is 5-6 px
            fontSize: '14px',
            position   : 'absolute',
            top        : 4,
            right      : 4,
            bgcolor    : 'rgba(0,0,0,0.54)',
            color      : '#fff',
            opacity    : 0,                      // start hidden
            transition : 'opacity 0.2s',
            '&:hover'  : { bgcolor: 'rgba(0,0,0,0.72)' },
          }}
        >
          <Close fontSize="inherit" />
        </IconButton>
      </Box>
    );
  }


  // Determine selected type hint
  const selectedType = localValues.allowedImageTypes[0];
  let dimensionHint = '';
  if (selectedType === 'icon') {
    dimensionHint = 'icons limited to max 250px wide × 150px tall';
  } else if (selectedType === 'banner') {
    dimensionHint = 'banners limited to Min 600×200px | Max 1920×600px';
  }

  return (
    <Box
      sx={{
        display        : 'flex',
        flexDirection  : 'column',
        alignItems     : 'center',
        justifyContent : 'center',
        minHeight      : 150,
        border         : '1px dashed',
        borderColor    : 'divider',
        borderRadius   : 2,
        p              : 2,
        backgroundColor: 'background.default',
        textAlign      : 'center',
        cursor         : 'pointer',
        transition     : theme => theme.transitions.create(['border-color', 'background-color']),
        '&:hover'      : {
          borderColor    : 'primary.main',
          backgroundColor: 'action.hover',
        },
      }}
      onClick={openAssetLibrary}
    >

     <Button
       variant="contained"
       color="primary"
       size="small"
       sx={{
         textTransform: 'none',
         px           : 2.5,
         fontWeight   : 'bold',
       }}
       onClick={openAssetLibrary}   // keep explicit for accessibility
     >
       + Add
     </Button>

      <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
        {dimensionHint}
      </Typography>
    </Box>
  );

};


  if (!selectedFieldForEdit || !selectedFieldForEdit.metadata) {
    return <Typography>Select a field to edit</Typography>;
  }

  return (
    <Box sx={{ maxWidth: '90%', mx: 'auto', mt: 2 }}>
      <Typography variant="body2" sx={{ ml: 1, mb: 1.5 }}>
        <strong>Field Type:</strong> {selectedFieldForEdit.metadata.type || 'image'}
      </Typography>

      {/* Buttons for selecting image type */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mb: 2 }}>
        <Button
          variant={localValues.allowedImageTypes.includes('icon') ? 'contained' : 'outlined'}
          onClick={() => handleChange('allowedImageTypes', 'icon')}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textTransform: 'none',
            width: '80px',
          }}
        >
          <ImageIcon />
          <Typography variant="caption">Icon</Typography>
        </Button>
        <Button
          variant={localValues.allowedImageTypes.includes('banner') ? 'contained' : 'outlined'}
          onClick={() => handleChange('allowedImageTypes', 'banner')}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textTransform: 'none',
            width: '80px',
          }}
        >
          <PanoramaIcon />
          <Typography variant="caption">Banner</Typography>
        </Button>
      </Box>

      {renderSelectedAsset()}

{isLibraryOpen && (
  <AssetLibrary
    isModal
    onClose={() => setIsLibraryOpen(false)}
onSelectAsset={(asset) => {
  console.log('[ImageFieldSettings: debug] Asset selected:', asset);
  applyAssetSelection(asset, null, 'asset-library');
  setIsLibraryOpen(false);
}}

    allowedImageTypes={localValues.allowedImageTypes}
  />
)}



      <AssetUploadDialog
        open={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
      onUploadSuccess={(uploadedAsset, intrinsicDims) => {
        console.log('[ImageFieldSettings: debug] Upload success:', uploadedAsset);
        applyAssetSelection(uploadedAsset, intrinsicDims, 'upload');
        setIsUploadDialogOpen(false);
      }}

        uploading={uploading}
        assetType={
          localValues.allowedImageTypes[0] === 'icon'
            ? 'icon'
            : localValues.allowedImageTypes[0] === 'banner'
              ? 'banner'
              : null // fallback if ever needed
        }
      />

      <Typography variant="body2" sx={{ mb: 2, mt: 4 }}>
        <strong>Style Settings</strong>
      </Typography>
      {/* MARGIN INPUT FIELDS (NEW) */}
<Box
  sx={{
    border: '1px solid',
    borderColor: 'divider', // Google Outline Style
    borderRadius: 2,
    p: 2,
    mb: 2,
    position: 'relative', // Enables correct positioning of the heading
  }}
>
  <Typography
    variant="caption"
    sx={{
      position: 'absolute',
      top: '-10px', // Moves the label on top like Google’s outline
      left: '10px',
      backgroundColor: 'background.paper', // Blends the label background with form background
      px: 1,
      fontWeight: 'bold',
      color: 'text.secondary',
    }}
  >
    Spacing Settings
  </Typography>

  {/* First Row - Top & Bottom Margins */}
  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
    <TextField
      fullWidth
      label="Top"
      type="number"
      value={parseInt(localValues.style.marginTop, 10)}
      onChange={(e) => handleStyleChange('marginTop', e.target.value)}
    />
    <TextField
      fullWidth
      label="Bottom"
      type="number"
      value={parseInt(localValues.style.marginBottom, 10)}
      onChange={(e) => handleStyleChange('marginBottom', e.target.value)}
    />
  </Box>

  {/* Second Row - Left & Right Margins */}
  <Box sx={{ display: 'flex', gap: 2 }}>
    <TextField
      fullWidth
      label="Left"
      type="number"
      value={parseInt(localValues.style.marginLeft, 10)}
      onChange={(e) => handleStyleChange('marginLeft', e.target.value)}
    />
    <TextField
      fullWidth
      label="Right"
      type="number"
      value={parseInt(localValues.style.marginRight, 10)}
      onChange={(e) => handleStyleChange('marginRight', e.target.value)}
    />
  </Box>
</Box>


      {/* Style Settings */}
<Box
  sx={{
    border: '1px solid',
    borderColor: 'divider', // Google Outline Style
    borderRadius: 2,
    p: 2,
    mb: 2,
    position: 'relative', // Enables correct positioning of the heading
  }}
>
  <Typography
    variant="caption"
    sx={{
      position: 'absolute',
      top: '-10px', // Moves the label on top like Google’s outline
      left: '10px',
      backgroundColor: 'background.paper', // Blends label with form background
      px: 1,
      fontWeight: 'bold',
      color: 'text.secondary',
    }}
  >
    Border Settings
  </Typography>

  {/* First Row - Border Color & Padding */}
  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
    <TextField
      fullWidth
      label="Color"
      value={localValues.style.borderColor}
      onChange={(e) => handleStyleChange('borderColor', e.target.value)}
    />
    <TextField
      fullWidth
      label="Border Width"
      type="number"
      value={parseInt(localValues.style.borderWidth, 10)}
      onChange={(e) => handleStyleChange('borderWidth', e.target.value)}
    />

  </Box>

  {/* Second Row - Border Width & Radius */}
  <Box sx={{ display: 'flex', gap: 2 }}>
    <TextField
      fullWidth
      label="Padding px"
      type="number"
      value={parseInt(localValues.style.padding, 10)}
      onChange={(e) => handleStyleChange('padding', e.target.value)}
    />
    <TextField
      fullWidth
      label="Radius"
      type="number"
      value={parseInt(localValues.style.borderRadius, 10)}
      onChange={(e) => handleStyleChange('borderRadius', e.target.value)}
    />
  </Box>
</Box>
    </Box>
  );
};

export default ImageFieldSettings;
